import { useState, useCallback, useRef, useEffect } from 'react'
import {
  getApplicationStatus,
  postYardiApplicationRetry,
} from '../services/yardiInviteService'
import type { ApplicationStatusResult } from '../types'
import { showSuccess, formatApiError } from '@/lib/utils/notifications'

const DEFAULT_POLL_INTERVAL_MS = 3000

export type MergeApplicationStatusResult = (
  applicationId: number,
  result: ApplicationStatusResult | null | undefined,
) => void

/** Shared retry + poll flow for invite list and guest search list. */
export function useYardiApplicationRetry(mergeFromStatusResult: MergeApplicationStatusResult) {
  const [retryingApplicationId, setRetryingApplicationId] = useState<number | null>(null)
  const [retryErrorModal, setRetryErrorModal] = useState<{ title: string; bodyHtml: string } | null>(
    null,
  )
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const stopPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current)
      pollingTimeoutRef.current = null
    }
  }, [])

  const clearRetryErrorModal = useCallback(() => setRetryErrorModal(null), [])

  const applicationStatusPolling = useCallback(
    (applicationId: number, interval: number) => {
      getApplicationStatus(applicationId)
        .then((result) => {
          mergeFromStatusResult(applicationId, result)
          const state =
            result && typeof result === 'object' && 'state' in result
              ? (result as ApplicationStatusResult).state
              : undefined
          if (state === 'INQUEUE') {
            pollingTimeoutRef.current = setTimeout(
              () => applicationStatusPolling(applicationId, interval),
              interval,
            )
          }
        })
        .catch(() => {
          /* no-op */
        })
    },
    [mergeFromStatusResult],
  )

  const retryapplication = useCallback(
    async (applicationId: number) => {
      setRetryErrorModal(null)
      setRetryingApplicationId(applicationId)
      try {
        await postYardiApplicationRetry(applicationId)
        showSuccess('Yardi application retry has been successfully triggered!')
        mergeFromStatusResult(applicationId, {
          state: 'INQUEUE',
          lr_check_error: null,
        })
        applicationStatusPolling(applicationId, DEFAULT_POLL_INTERVAL_MS)
      } catch (e) {
        setRetryErrorModal({ title: 'Error', bodyHtml: formatApiError(e) })
      } finally {
        setRetryingApplicationId(null)
      }
    },
    [applicationStatusPolling, mergeFromStatusResult],
  )

  useEffect(() => () => stopPolling(), [stopPolling])

  return {
    retryingApplicationId,
    isRetryCallOngoing: retryingApplicationId != null,
    retryapplication,
    retryErrorModal,
    clearRetryErrorModal,
  }
}
